// src/templateManager/components/tabs/OverviewTab.jsx
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Card,
  Stack,
  TextInput,
  Textarea,
  Checkbox,
  Text,
  Badge,
  Group,
  Divider
} from '@mantine/core';
import { IconRobot, IconBrain, IconSettings, IconTool, IconCompass, IconGlobe } from '@tabler/icons-react';

/**
 * OverviewTab - Edit template metadata
 * Name, description, model type, default status
 */
function OverviewTab() {
  const { control, formState: { errors }, watch, setValue } = useFormContext();

  const modelType = watch('modelType');
  const isDefault = watch('isDefault');

  // Derive assessment category from modelType
  const assessmentCategory = modelType === 'use_case' ? 'navigator'
    : modelType === 'business_case' ? 'explorer'
    : 'builder';

  // Assessment category options
  const assessmentCategoryOptions = [
    {
      value: 'builder',
      label: 'Builder',
      description: 'Technical audit of a specific model type',
      icon: <IconTool size={20} />,
      color: 'blue'
    },
    {
      value: 'navigator',
      label: 'Navigator',
      description: 'Use-case scenario risks — applies to any model',
      icon: <IconCompass size={20} />,
      color: 'orange'
    },
    {
      value: 'explorer',
      label: 'Explorer',
      description: 'Business impact analysis — applies to any model',
      icon: <IconGlobe size={20} />,
      color: 'green'
    }
  ];

  // Model type options (only relevant for Builder)
  const modelTypeOptions = [
    {
      value: 'generative',
      label: 'Generative AI',
      description: 'LLMs, GPT models, text/image generation systems',
      icon: <IconRobot size={20} />
    },
    {
      value: 'predictive',
      label: 'Predictive AI',
      description: 'ML models, classifiers, regression systems',
      icon: <IconBrain size={20} />
    },
    {
      value: 'custom',
      label: 'Custom',
      description: 'Custom or hybrid AI model type',
      icon: <IconSettings size={20} />
    }
  ];

  return (
    <Stack gap="lg">
      <Card withBorder p="lg">
        <Stack gap="md">
          <Text size="lg" fw={700}>Template Information</Text>

          {/* Template Name */}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Template Name"
                placeholder="e.g., Generative AI Assessment"
                required
                error={errors.name?.message}
                description="A clear, descriptive name for this assessment template"
              />
            )}
          />

          {/* Description */}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label="Description"
                placeholder="Describe what this template is for, which model types it covers, and key assessment areas..."
                minRows={4}
                maxRows={8}
                error={errors.description?.message}
                description="Help Risk Managers understand when to use this template"
              />
            )}
          />

          <Divider />

          {/* Assessment Category */}
          <div>
            <Text size="sm" fw={500} mb="xs">
              Assessment Type <Text component="span" c="red">*</Text>
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              Choose what kind of assessment this template is for
            </Text>

            <Group gap="sm" grow>
              {assessmentCategoryOptions.map((option) => (
                <Card
                  key={option.value}
                  withBorder
                  p="md"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: assessmentCategory === option.value ? `var(--mantine-color-${option.color}-0)` : undefined,
                    borderColor: assessmentCategory === option.value ? `var(--mantine-color-${option.color}-5)` : undefined,
                    textAlign: 'center'
                  }}
                  onClick={() => {
                    if (option.value === 'navigator') setValue('modelType', 'use_case');
                    else if (option.value === 'explorer') setValue('modelType', 'business_case');
                    else setValue('modelType', 'generative'); // default Builder to generative
                  }}
                >
                  <Stack gap="xs" align="center">
                    {option.icon}
                    <Text fw={600} c={assessmentCategory === option.value ? option.color : 'dimmed'}>{option.label}</Text>
                    <Text size="xs" c="dimmed">{option.description}</Text>
                    {assessmentCategory === option.value && (
                      <Badge color={option.color} size="sm">Selected</Badge>
                    )}
                  </Stack>
                </Card>
              ))}
            </Group>
          </div>

          {/* Model Type - Only for Builder templates */}
          {assessmentCategory === 'builder' && (
            <div>
              <Text size="sm" fw={500} mb="xs">
                Model Type <Text component="span" c="red">*</Text>
              </Text>
              <Text size="xs" c="dimmed" mb="md">
                Select the AI model type this Builder template is designed for
              </Text>

              <Controller
                name="modelType"
                control={control}
                render={({ field }) => (
                  <Stack gap="sm">
                    {modelTypeOptions.map((option) => (
                      <Card
                        key={option.value}
                        withBorder
                        p="md"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: field.value === option.value ? 'var(--mantine-color-blue-0)' : undefined,
                          borderColor: field.value === option.value ? 'var(--mantine-color-blue-5)' : undefined
                        }}
                        onClick={() => field.onChange(option.value)}
                      >
                        <Group>
                          {option.icon}
                          <div style={{ flex: 1 }}>
                            <Text fw={600}>{option.label}</Text>
                            <Text size="sm" c="dimmed">{option.description}</Text>
                          </div>
                          {field.value === option.value && (
                            <Badge color="blue">Selected</Badge>
                          )}
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                )}
              />
              {errors.modelType && (
                <Text size="sm" c="red" mt="xs">{errors.modelType.message}</Text>
              )}
            </div>
          )}

          <Divider />

          {/* Default Template */}
          <Controller
            name="isDefault"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onChange={(event) => field.onChange(event.currentTarget.checked)}
                label={
                  <div>
                    <Text size="sm" fw={500}>Set as default template for {assessmentCategory === 'builder' ? (modelTypeOptions.find(o => o.value === modelType)?.label || 'this model type') : assessmentCategoryOptions.find(o => o.value === assessmentCategory)?.label} assessments</Text>
                    <Text size="xs" c="dimmed">
                      This template will be automatically selected when creating {assessmentCategory === 'builder' ? 'Builder assessments for this model type' : `${assessmentCategoryOptions.find(o => o.value === assessmentCategory)?.label} assessments`}
                    </Text>
                  </div>
                }
              />
            )}
          />

          {isDefault && (
            <Card withBorder p="sm" bg="yellow.0">
              <Text size="sm" c="yellow.9">
                ⚠️ Setting this as default will unset any other default template for {assessmentCategory === 'builder' ? (modelTypeOptions.find(o => o.value === modelType)?.label || 'this') : assessmentCategoryOptions.find(o => o.value === assessmentCategory)?.label} assessments
              </Text>
            </Card>
          )}
        </Stack>
      </Card>

      {/* Taxonomy Summary (Read-only preview) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Text size="lg" fw={700}>Taxonomy Summary</Text>
          <Text size="sm" c="dimmed">
            Configure the complete taxonomy in the dedicated tabs above
          </Text>

          <Group gap="md">
            <Badge size="lg" variant="light">
              Context Fields: 0 configured
            </Badge>
            <Badge size="lg" variant="light">
              Risk Areas: 0 configured
            </Badge>
            <Badge size="lg" variant="light">
              Security Categories: 0 configured
            </Badge>
          </Group>

          <Text size="xs" c="dimmed">
            Switch to other tabs to configure context fields, risk areas, root causes, and security taxonomy
          </Text>
        </Stack>
      </Card>

      {/* Evaluation Criteria Reference (Read-only) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="lg" fw={700}>Evaluation Criteria</Text>
            <Badge size="sm" color="blue">Central Config</Badge>
          </Group>

          <Text size="sm" c="dimmed">
            This template uses centrally managed evaluation criteria to ensure uniform risk scoring across all assessments.
          </Text>

          <Stack gap="xs">
            <Group>
              <Badge variant="dot" color="green">Risk Evaluation</Badge>
              <Text size="sm">Default Risk Evaluation Criteria</Text>
            </Group>
            <Group>
              <Badge variant="dot" color="green">Control Evaluation</Badge>
              <Text size="sm">Default Control Evaluation Criteria</Text>
            </Group>
          </Stack>

          <Text size="xs" c="dimmed">
            To modify evaluation criteria, use the Evaluation Criteria Manager (separate from templates)
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

export default OverviewTab;
